/**
 * "Preview what the recipient sees" (§A6f) — a disclosure on the bid editor that
 * renders the exact recipient-facing budget box from the live bid inputs, so the
 * supervisor knows precisely what's sent when cost is included. Uses the same
 * BudgetCoverage component as the public approval page (no drift).
 */
import { useState } from 'react';
import { ChevronDown, Eye } from 'lucide-react';
import type { CostSettings, LaborRates } from '@vari-crew/schemas';
import { summarizeBid, type BidInputs } from '@/components/ui/BidBuilder';
import { BudgetCoverage } from '@/components/bid/BudgetCoverage';
import { cn } from '@/lib/cn';

export function RecipientPreview({
  bid,
  showCost,
  rates,
  settings,
  defaultOpen = false,
}: {
  bid: BidInputs;
  showCost: boolean;
  rates: LaborRates | null;
  settings: CostSettings | null;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const s = summarizeBid(bid, rates, settings);
  return (
    <div className="rounded-lg border border-dashed border-line">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-ink-dim hover:bg-fill-soft rounded-lg"
      >
        <span className="flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" /> Preview what the recipient sees
        </span>
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="px-3 pb-3">
          <BudgetCoverage
            showCost={showCost}
            crew={s.crew}
            manHours={s.manHours}
            roles={bid.labor_roles}
            includesMileage={s.includesMileage}
            includesDebris={s.includesDebris}
            totalCents={s.totalCents}
          />
        </div>
      )}
    </div>
  );
}
